// The one door between the screens and the data. C-F1/C-F2: the fixture (the demo engine in the browser). After
// the lock: HttpApi (the backend), swapped in main.tsx without touching a screen. Every method is async and
// throws ApiError with an HTTP-like status, as the backend will.
import type {
  AudiencesApi,
  BatchesApi,
  CampaignsApi,
  CampaignWizardApi,
  ClassificationApi,
  Clock,
  ImagesApi,
  IvrNumbersApi,
  LeadsApi,
  LenderHubApi,
  LogApi,
  NumbersApi,
  OverviewApi,
  Session,
  SessionApi,
  SettingsApi,
  SourcesApi,
  SuppressionApi,
  TemplatesApi,
  User,
  UsersApi,
  VendorApisApi,
  VendorChangesApi,
  VendorsApi,
  VoiceMessagesApi,
  WorkflowsApi,
} from './schemas'

/** 401 not signed in · 403 your role may not · 404 not found · 422 refused by a rule (the message says why). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface TestUser {
  user: User
  /** the password that works now: the seed's test password, or the one the person chose (mock only) */
  password: string
  /** the 6-digit code right now when 2-step is on (the mock knows the key; a real backend never shows it) */
  code: string | null
}

/**
 * Test tools of the mock API (the "Test tools" panel). The real backend has none of this: with it `api.demo` is
 * absent and the panel is not there. The "Simulate" buttons stand in for what a vendor or Meta sends us later
 * (template approved, callbacks).
 */
export interface DemoControls {
  listTestUsers(): Promise<TestUser[]>
  /** straight in, skipping password and 2-step (testing only) */
  signInAs(userId: string): Promise<Session>
  advanceClock(hours: number): Promise<Clock>
  /** always the next calendar day at 10:00 */
  advanceToNextDayTen(): Promise<Clock>
  resetDemo(): Promise<void>
  simulateTemplateDecision(templateId: string, decision: 'approved' | 'rejected'): Promise<void>
}

export interface CampaignApi
  extends
    SessionApi,
    UsersApi,
    SettingsApi,
    OverviewApi,
    NumbersApi,
    TemplatesApi,
    IvrNumbersApi,
    VoiceMessagesApi,
    VendorsApi,
    VendorApisApi,
    VendorChangesApi,
    LenderHubApi,
    SourcesApi,
    LeadsApi,
    SuppressionApi,
    AudiencesApi,
    WorkflowsApi,
    ClassificationApi,
    BatchesApi,
    CampaignsApi,
    CampaignWizardApi,
    ImagesApi,
    LogApi {
  demo?: DemoControls
}
